/**
 * Turn an ordered list of clicked wall points into run segments.
 * Pure. Each click = { wall, point } where `wall` carries
 * `alignment.bottomLeft/bottomRight` and `point` is the world {x,z} on it.
 *
 * Consecutive clicks on the SAME wall form one segment. When two consecutive
 * clicks are on DIFFERENT walls, we infer the corner as the intersection of
 * the two wall lines and split into two segments that share that exact corner
 * vertex — so the engine's corner detector (which needs the shared vertex to
 * coincide within 1 mm) fires reliably even though the user's click never
 * lands exactly on the geometric corner.
 *
 * See claude-docs/2026-06-13-manual-cabinet-runs-plan-05-draw-tool.md.
 */

const { worldToWallParam } = require('../kitchen-assembly/wallMath');
const { JOIN_EPS, isStraightOnTurn, turnDeg } = require('./bendGeometry');

const ZERO_LEN_EPS = 1e-4; // meters — drop slivers (e.g. a click already on the corner)
// Free-standing legs shorter than this are click artifacts — most commonly the
// duplicate end point a finishing double-click tacks on (a ~1 cm perpendicular
// stub). No real island/peninsula leg is shorter than a single narrow cabinet.
const MIN_FREE_SEGMENT_LEN = 0.05; // meters (~2")

/** Intersection of two walls' infinite centerlines, or null if parallel. */
function wallLineIntersection(wallA, wallB) {
  const a = wallA.alignment?.bottomLeft;
  const b = wallA.alignment?.bottomRight;
  const c = wallB.alignment?.bottomLeft;
  const d = wallB.alignment?.bottomRight;
  if (!a || !b || !c || !d) return null;
  const r = { x: b.x - a.x, z: b.z - a.z };
  const s = { x: d.x - c.x, z: d.z - c.z };
  const denom = r.x * s.z - r.z * s.x;
  if (Math.abs(denom) < 1e-9) return null; // parallel / collinear
  const t = ((c.x - a.x) * s.z - (c.z - a.z) * s.x) / denom;
  return { x: a.x + t * r.x, z: a.z + t * r.z };
}

function seg(wall, fromPoint, toPoint) {
  return {
    wall,
    startParam: worldToWallParam(wall, fromPoint),
    endParam: worldToWallParam(wall, toPoint),
  };
}

/**
 * Two consecutive free legs that meet and turn less than the draw tool's snap
 * catch (`BEND_SNAP_DEG`). The snap straightens any such turn, so they are one
 * leg drawn with an extra click — and a bend of 0° is no bend to fill.
 */
function freeLegsRunStraightOn(a, b) {
  if (!a?.freestanding || !b?.freestanding) return false;
  if (Math.hypot(a.end.x - b.start.x, a.end.z - b.start.z) > JOIN_EPS) {
    return false;
  }
  const dir = (s) => {
    const len = Math.hypot(s.end.x - s.start.x, s.end.z - s.start.z);
    return len > 1e-9
      ? { x: (s.end.x - s.start.x) / len, z: (s.end.z - s.start.z) / len }
      : null;
  };
  const dA = dir(a);
  const dB = dir(b);
  if (!dA || !dB) return false;
  return isStraightOnTurn(turnDeg(dA, dB));
}

function buildRunSegments(clicks) {
  if (!Array.isArray(clicks) || clicks.length < 2) return [];

  const raw = [];
  for (let i = 0; i < clicks.length - 1; i += 1) {
    const a = clicks[i];
    const b = clicks[i + 1];

    if (!a.wall || !b.wall) {
      // Free-standing leg: the backing line is the two click points themselves.
      raw.push({ freestanding: true, start: a.point, end: b.point });
      continue;
    }

    if (a.wall.id === b.wall.id) {
      raw.push(seg(a.wall, a.point, b.point));
      continue;
    }

    // Wall change: infer the shared corner and split into two legs.
    const corner = wallLineIntersection(a.wall, b.wall);
    if (corner) {
      raw.push(seg(a.wall, a.point, corner));
      raw.push(seg(b.wall, corner, b.point));
    } else {
      // Parallel walls (no corner) — keep a single span on the second wall.
      raw.push(seg(b.wall, a.point, b.point));
    }
  }

  // Merge consecutive segments on the same wall. A middle click that lands
  // near a corner produces a tiny sliver leg + the real run on the same wall;
  // keeping them separate makes the corner reservation land on the sliver
  // (which then gets skipped as too short), leaving the real run unreserved
  // and its cabinets overrunning into the corner cabinet. Merging keeps the
  // run as one segment so the corner reservation lands on it.
  const merged = [];
  for (const s of raw) {
    const last = merged[merged.length - 1];
    if (last && last.wall && s.wall && last.wall.id === s.wall.id) {
      last.endParam = s.endParam; // contiguous same-wall run → extend
    } else if (freeLegsRunStraightOn(last, s)) {
      last.end = s.end; // the snap made them one straight leg → one leg
    } else {
      merged.push({ ...s });
    }
  }

  // Drop near-zero-length slivers: wall legs that collapse onto a corner, and
  // free-standing stubs from a duplicate finishing click (see MIN_FREE_SEGMENT_LEN).
  return merged.filter((s) =>
    s.freestanding
      ? Math.hypot(s.end.x - s.start.x, s.end.z - s.start.z) >
        MIN_FREE_SEGMENT_LEN
      : Math.abs(s.endParam - s.startParam) > ZERO_LEN_EPS
  );
}

module.exports = {
  buildRunSegments,
  wallLineIntersection,
  MIN_FREE_SEGMENT_LEN,
};
